import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/layout/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { site } from '@/mocks/columns';

const FORM_URL = 'https://readdy.ai/api/form/dae3gj1tto0ceqqngos0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: '联系看料社编辑部',
  url: `${site.domain}/lianxi`,
  inLanguage: 'zh-CN',
  mainEntity: {
    '@type': 'Organization',
    name: '看料社',
    email: site.email,
  },
};

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honey = String(data.get('website_alt') || '').trim();
    const payload = new FormData();
    if (!honey) {
      payload.set('name', String(data.get('name') || '').trim());
      payload.set('email', String(data.get('email') || '').trim());
      payload.set('type', String(data.get('type') || '').trim());
      payload.set('message', String(data.get('message') || '').trim());
    }

    setStatus('submitting');
    setMessage('');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const okCode = parsed?.code === 'OK';

      if (honey) {
        setStatus('success');
        setMessage('已收到，谢谢你的来信。');
        return;
      }

      if (res.ok && okCode) {
        setStatus('success');
        setMessage('来信已收到！编辑部会在 1-2 个工作日内回复你。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg && serverMsg !== 'OK' ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络似乎不太顺畅，请稍后重试。');
    }
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="联系我们 · 投稿线索/栏目建议 - 看料社编辑部"
        description="联系看料社编辑部：提供深扒线索、栏目建议、合作与媒体交流。填写联系表单或发送邮件至编辑部，我们通常会在 1-2 个工作日内回复。"
        keywords="联系看料社,看料社投稿,深扒线索,栏目建议,看料社编辑部邮箱"
        canonical={`${site.domain}/lianxi`}
        jsonLd={jsonLd}
      />
      <Header />

      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="text-xs text-foreground-500" aria-label="面包屑">
              <ol className="flex flex-wrap items-center gap-1">
                <li>
                  <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
                </li>
                <li aria-hidden="true" className="text-foreground-400">/</li>
                <li className="text-foreground-800" aria-current="page">联系我们</li>
              </ol>
            </nav>
            <div className="mt-6 max-w-3xl">
              <p className="lede-mark text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
                Contact
              </p>
              <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                联系看料社编辑部
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                无论是深扒线索、栏目建议，还是对某篇文章的补充与指正，我们都欢迎。来信请尽量把时间、信源讲清楚，能大大提高被采纳与核实的效率。
              </p>
            </div>
          </div>
        </section>

        <section className="border-b border-background-200">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-5">
            {/* 联系信息 */}
            <aside className="space-y-4 lg:col-span-2">
              <h2 className="font-heading text-lg font-bold text-foreground-950">其他联系渠道</h2>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                  <i className="ri-mail-line text-primary-600" aria-hidden="true" />
                  编辑部邮箱
                </p>
                <a href={`mailto:${site.email}`} className="mt-1 block break-all text-sm text-primary-600 hover:text-primary-800">
                  {site.email}
                </a>
                <p className="mt-3 border-t border-background-200 pt-3 text-xs leading-relaxed text-foreground-500">
                  邮件主题建议写明类型，例如"深扒线索 / 栏目建议 / 合作交流"，方便编辑部分类处理。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                  <i className="ri-time-line text-primary-600" aria-hidden="true" />
                  回复时间
                </p>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                  编辑部通常会在 1-2 个工作日内回复来信。投稿数量较多时可能略有延迟，请见谅。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                  <i className="ri-shield-check-line text-primary-600" aria-hidden="true" />
                  信源与隐私
                </p>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                  我们会妥善处理你的线索与个人信息。如需匿名，请在来信中注明，我们会在稿面与核实环节一并处理。
                </p>
              </div>
            </aside>

            {/* 表单 */}
            <div className="lg:col-span-3">
              <form
                onSubmit={handleSubmit}
                data-readdy-form="看料社联系表单"
                className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
                noValidate
              >
                <h2 className="font-heading text-lg font-bold text-foreground-950">来信表单</h2>
                <p className="mt-1 text-xs text-foreground-500">带 * 的为必填项</p>

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      称呼 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      联系邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="type" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    来信类型 <span className="text-primary-600">*</span>
                  </label>
                  <select
                    id="type"
                    name="type"
                    required
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="">请选择类型</option>
                    <option value="深扒线索">深扒线索 / 爆料</option>
                    <option value="栏目建议">栏目建议</option>
                    <option value="指正补充">对文章内容的指正或补充</option>
                    <option value="合作交流">合作 / 媒体交流</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    留言内容 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    placeholder="请尽量写清楚时间、信源与来龙去脉……（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                  <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
                </div>

                {/* 反垃圾字段 */}
                <div className="field-note">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex items-center whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin text-base" aria-hidden="true" />
                        发送中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2 text-sm" aria-hidden="true" />
                        发送来信
                      </>
                    )}
                  </button>
                </div>

                {status === 'success' && (
                  <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base" aria-hidden="true" />
                    {message}
                  </p>
                )}
                {status === 'error' && (
                  <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-900">
                    <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true" />
                    {message}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}